import { useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { Avatar } from "@/components/common/Avatar";
import { NoBackend } from "@/components/common/NoBackend";
import { buttonClasses } from "@/components/design/button-classes";
import { Hero } from "@/components/layout/Hero";
import { GrowDashboardDocument } from "@/graphql/generated/operations";
import { ELEMENT_CODES } from "@/lib/elements";
import { rankLabel } from "@/lib/grow-rank";
import { POWER_BY_ABBR, TAG_LABEL_IDS } from "@/lib/power-library";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";
import { useAgentStore } from "@/stores/agent-store";
import type { IntlShape } from "react-intl";

// Grow landing - a dashboard over the user's real grow data (GrowDashboard
// query): overall progress + rank, the next assessments to tackle, strongest
// and weakest powers, and where the user stands among contacts. Below the live
// dashboard sit the still-unbuilt sections (Live-Hacks, games, Circus
// Experten-Stories) as comingSoon previews of the pillar roadmap.

type SelfResult = {
  element: string;
  result?: number | null;
  questionCount: number;
  answeredQuestionCount: number;
};

// element enum (e.g. "PERSEVERANCE") → { abbr, name, group } so a result row
// can render the localized power name, its 3-letter code and its cluster, and
// link to the detail page.
function powerOf(
  element: string,
  intl: IntlShape,
): { abbr: string; name: string; group: string } | null {
  const abbr = ELEMENT_CODES[element];
  if (!abbr) return null;
  const power = POWER_BY_ABBR[abbr];
  return power
    ? {
        abbr,
        name: intl.formatMessage({ id: power.nameId }),
        group: intl.formatMessage({ id: TAG_LABEL_IDS[power.tag] }),
      }
    : null;
}

export function GrowPage() {
  const intl = useIntl();
  const ask = useAgentStore((s) => s.ask);
  const { data, loading } = useQuery(GrowDashboardDocument, {
    fetchPolicy: "cache-and-network",
  });

  const ranking = data?.growRanking;
  const results = useMemo<SelfResult[]>(
    () => (data?.selfAssessmentResults ?? []).map((r) => ({ ...r })),
    [data],
  );

  // Open assessments: not yet fully answered, closest-to-completion first so
  // the quickest wins surface. Pending (0 answered) count as open too.
  const focus = useMemo(
    () =>
      results
        .filter((r) => r.questionCount > 0 && r.answeredQuestionCount < r.questionCount)
        .sort((a, b) => b.answeredQuestionCount - a.answeredQuestionCount)
        .slice(0, 4),
    [results],
  );

  // Strengths / weaknesses: only powers with a numeric self-assessment score.
  const scored = useMemo(
    () =>
      results
        .filter((r): r is SelfResult & { result: number } => typeof r.result === "number")
        .sort((a, b) => b.result - a.result),
    [results],
  );
  const strengths = scored.slice(0, 3);
  const weaknesses = scored.slice(-3).reverse();

  const contacts = data?.contactsWithHighestScore ?? [];

  // Guided tour (/tour): summary + the dashboard's cards, with the user's live numbers.
  useAssistantTour(
    data
      ? {
          label: intl.formatMessage({
            id: "grow.tour.label",
            defaultMessage: "Grow",
          }),
          summary: intl.formatMessage(
            {
              id: "grow.tour.summary",
              defaultMessage:
                "This is your Grow dashboard. Your overall power score is {score}%.",
            },
            { score: ranking?.score ?? 0 },
          ),
          stops: [
            {
              anchor: "grow.powerMap",
              title: intl.formatMessage({ id: "grow.tour.progress.title", defaultMessage: "Profile progress" }),
              description: intl.formatMessage(
                {
                  id: "grow.tour.progress.desc",
                  defaultMessage:
                    "Your overall progress: {score}%{rank, select, none {} other { (rank: {rank})}}. Answering power questions raises it. Click 'Power Map' to see all 30 powers.",
                },
                {
                  score: ranking?.score ?? 0,
                  rank: ranking?.userRank ? rankLabel(ranking.userRank, intl.locale) : "none",
                },
              ),
            },
            {
              anchor: "grow.upNext",
              title: intl.formatMessage({ id: "grow.tour.upNext.title", defaultMessage: "Up next" }),
              description: intl.formatMessage(
                {
                  id: "grow.tour.upNext.desc",
                  defaultMessage:
                    "{count, plural, =0 {All self-assessments are done. Nothing is open.} other {You have {count} open self-assessments; the ones closest to completion are listed first. Click one to continue rating.}}",
                },
                { count: focus.length },
              ),
            },
            {
              anchor: "grow.strengths",
              title: intl.formatMessage({ id: "grow.tour.strengths.title", defaultMessage: "Your strengths" }),
              description: intl.formatMessage(
                {
                  id: "grow.tour.strengths.desc",
                  defaultMessage:
                    "{count, plural, =0 {Once you rate powers, your top ones appear here.} other {Your strongest powers right now: {names}.}}",
                },
                {
                  count: strengths.length,
                  names: strengths.map((r) => powerOf(r.element, intl)?.name ?? r.element).join(", "),
                },
              ),
            },
            {
              anchor: "grow.roomToGrow",
              title: intl.formatMessage({ id: "grow.tour.room.title", defaultMessage: "Room to grow" }),
              description: intl.formatMessage(
                {
                  id: "grow.tour.room.desc",
                  defaultMessage:
                    "{count, plural, =0 {Your weakest rated powers will appear here.} other {Your lowest-rated powers: {names}. The button below asks me for improvement ideas.}}",
                },
                {
                  count: weaknesses.length,
                  names: weaknesses.map((r) => powerOf(r.element, intl)?.name ?? r.element).join(", "),
                },
              ),
            },
          ],
        }
      : null,
  );

  // Live assistant context (motive 1, ADR 0020): the user's own grow standing on this dashboard.
  useAssistantContext(
    data
      ? [
          {
            description: "Grow dashboard the user is viewing",
            value:
              `overall power score ${ranking?.score ?? "n/a"}%` +
              (ranking?.userRank ? `, rank ${rankLabel(ranking.userRank, intl.locale)}` : "") +
              `; ${focus.length} open self-assessments; ` +
              `strengths: ${strengths.map((r) => powerOf(r.element, intl)?.name ?? r.element).join(", ") || "none yet"}; ` +
              `weaknesses: ${weaknesses.map((r) => powerOf(r.element, intl)?.name ?? r.element).join(", ") || "none yet"}`,
          },
        ]
      : null,
  );

  return (
    <main
      className="w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10"
      data-graphql="GrowDashboard"
    >
      <Hero
        title="GROW"
        subtitle={
          <>
            <FormattedMessage
              id="grow.landing.heroSubtitleLine1"
              defaultMessage="Discover playfully,"
            />
            <br />
            <FormattedMessage
              id="grow.landing.heroSubtitleLine2"
              defaultMessage="what makes you really tick."
            />
          </>
        }
        body={intl.formatMessage({
          id: "grow.landing.heroBody",
          defaultMessage:
            "Quests, games, micro-exercises. Your personal power playground. Move powers without measuring them.",
        })}
        image={{ src: "/heroes/grow.png" }}
      />

      <div className="mt-8 grid grid-cols-1 gap-4 @lg:grid-cols-3">
        {/* Progress + rank */}
        <Card
          label={intl.formatMessage({
            id: "grow.landing.profileProgress",
            defaultMessage: "Profile progress",
          })}
          to="/grow/map"
          anchor="grow.powerMap"
          right={
            <FormattedMessage
              id="grow.landing.powerMap"
              defaultMessage="Power Map ↗"
            />
          }
        >
          {ranking ? (
            <div>
              <div className="font-serif text-5xl leading-none tabular-nums">
                {ranking.score}&thinsp;%
              </div>
              <div className="mt-2 text-[13px] text-ink/55">
                {rankLabel(ranking.userRank, intl.locale)}
              </div>
              <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-ink/10">
                <div
                  className="h-full rounded-full bg-accent"
                  style={{ width: `${ranking.score}%` }}
                />
              </div>
              <p className="mt-3 text-[11px] leading-snug text-ink/45">
                <FormattedMessage
                  id="grow.landing.progressHint"
                  defaultMessage="Answer all questions about the powers to increase your profile progress."
                />
              </p>
            </div>
          ) : (
            <Empty loading={loading} />
          )}
        </Card>

        {/* Focus today / open assessments */}
        <Card
          label={intl.formatMessage({
            id: "grow.landing.upNext",
            defaultMessage: "Up next",
          })}
          anchor="grow.upNext"
          right={
            <FormattedMessage
              id="grow.landing.openCount"
              defaultMessage="{count} open"
              values={{ count: focus.length }}
            />
          }
        >
          {focus.length ? (
            <ul className="space-y-1.5">
              {focus.map((r) => {
                const p = powerOf(r.element, intl);
                if (!p) return null;
                return (
                  <li key={r.element}>
                    <Link
                      to={`/grow/power/${p.abbr}`}
                      className="flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 transition hover:bg-ink/[0.06]"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="truncate text-[13px]">{p.name}</span>
                        <span className="mt-0.5 block text-[10px] tracking-[0.15em] text-ink/40">
                          {p.abbr} · {p.group}
                        </span>
                      </span>
                      <span className="shrink-0 text-[11px] tabular-nums text-ink/45">
                        {r.answeredQuestionCount}/{r.questionCount}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          ) : (
            <Empty
              loading={loading}
              done={intl.formatMessage({
                id: "grow.landing.allAssessmentsDone",
                defaultMessage: "All self-assessments completed.",
              })}
            />
          )}
        </Card>

        {/* Contact leaderboard */}
        <Card
          label={intl.formatMessage({
            id: "grow.landing.inComparison",
            defaultMessage: "In comparison",
          })}
          right={
            <FormattedMessage
              id="grow.landing.community"
              defaultMessage="Community"
            />
          }
        >
          {contacts.length ? (
            <ol className="space-y-1.5">
              {contacts.map((c, i) => (
                <li key={c.person.id}>
                  <Link
                    to={`/p/${c.person.id}`}
                    title={intl.formatMessage(
                      {
                        id: "grow.landing.openProfile",
                        defaultMessage: "Open {name}",
                      },
                      {
                        name:
                          c.person.formattedName ??
                          intl.formatMessage({
                            id: "grow.landing.profileFallback",
                            defaultMessage: "profile",
                          }),
                      },
                    )}
                    className="flex items-center gap-3 rounded-md px-2 py-1 transition hover:bg-ink/[0.06]"
                  >
                    <span className="w-4 shrink-0 text-[11px] tabular-nums text-ink/40">
                      {i + 1}
                    </span>
                    <Avatar
                      size="sm"
                      person={{
                        name: c.person.formattedName ?? "",
                        avatarContent: c.person.avatar?.content,
                        avatarMime: c.person.avatar?.mimeType,
                      }}
                    />
                    <span className="min-w-0 flex-1 truncate text-[13px]">
                      {c.person.formattedName ?? "–"}
                    </span>
                    <span className="shrink-0 text-[11px] tabular-nums text-ink/55">
                      {c.score}&thinsp;%
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          ) : (
            <Empty
              loading={loading}
              done={intl.formatMessage({
                id: "grow.landing.noCommunityProgress",
                defaultMessage: "No community with grow progress yet.",
              })}
            />
          )}
        </Card>
      </div>

      {/* Strengths & weaknesses */}
      <div className="mt-4 grid grid-cols-1 gap-4 @lg:grid-cols-2">
        <Card
          label={intl.formatMessage({
            id: "grow.landing.yourStrengths",
            defaultMessage: "Your strengths",
          })}
          anchor="grow.strengths"
        >
          <PowerScoreList rows={strengths} loading={loading} />
        </Card>
        <Card
          label={intl.formatMessage({
            id: "grow.landing.roomToGrow",
            defaultMessage: "Room to grow",
          })}
          anchor="grow.roomToGrow"
        >
          <PowerScoreList
            rows={weaknesses}
            loading={loading}
            onAsk={(name) => {
              const v = Math.floor(Math.random() * 10);
              ask(intl.formatMessage({ id: `grow.powerDetail.askImprove.${v}` }, { name }));
            }}
          />
          {weaknesses.length ? (
            <button
              type="button"
              onClick={() => {
                const powers = weaknesses
                  .map((w) => powerOf(w.element, intl)?.name)
                  .filter(Boolean)
                  .join(", ");
                const v = Math.floor(Math.random() * 10);
                ask(intl.formatMessage({ id: `grow.landing.focusAskIntro.${v}` }, { powers }));
              }}
              className={buttonClasses({ variant: "primary", size: "compact", className: "mt-3" })}
            >
              <FormattedMessage id="grow.landing.focusAskButton" />
            </button>
          ) : null}
        </Card>
      </div>

      {/* Roadmap preview - pillar sections not built yet. */}
      <Section
        label={intl.formatMessage({
          id: "grow.landing.liveHacks",
          defaultMessage: "Live hacks",
        })}
        right={
          <FormattedMessage
            id="grow.landing.viewAll"
            defaultMessage="View all ↗"
          />
        }
      >
        <NoBackend
          label={intl.formatMessage({
            id: "grow.landing.liveHacks",
            defaultMessage: "Live hacks",
          })}
          comingSoon
        />
      </Section>

      <Section
        label={intl.formatMessage({
          id: "grow.landing.gamesExercises",
          defaultMessage: "Games · Playful learning exercises",
        })}
        right={
          <FormattedMessage
            id="grow.landing.viewAll"
            defaultMessage="View all ↗"
          />
        }
      >
        <NoBackend
          label={intl.formatMessage({
            id: "grow.landing.gamesExercises",
            defaultMessage: "Games · Playful learning exercises",
          })}
          comingSoon
        />
      </Section>

      <Section
        label={intl.formatMessage({
          id: "grow.landing.circusStories",
          defaultMessage: "Circus · Expert stories",
        })}
        right={
          <FormattedMessage
            id="grow.landing.viewAll"
            defaultMessage="View all ↗"
          />
        }
      >
        <NoBackend
          label={intl.formatMessage({
            id: "grow.landing.circusStories",
            defaultMessage: "Circus · Expert stories",
          })}
          comingSoon
        />
      </Section>

    </main>
  );
}

function Section({
  label,
  right,
  children,
}: {
  label: string;
  right: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="mt-10">
      <div className="mb-3 flex items-baseline justify-between">
        <div className="text-[10px] tracking-[0.25em] text-ink/55">{label}</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-ink/40">{right}</div>
      </div>
      {children}
    </div>
  );
}

function PowerScoreList({
  rows,
  loading,
  onAsk,
}: {
  rows: Array<SelfResult & { result: number }>;
  loading: boolean;
  // When set, each row shows a small assistant icon that asks for help improving that power.
  onAsk?: (name: string) => void;
}) {
  const intl = useIntl();
  // Rows whose assistant icon was clicked → active state (mirrors the question-explainer icon: flipped
  // mascot + filled/ringed background), keyed by element.
  const [askedIds, setAskedIds] = useState<ReadonlySet<string>>(() => new Set());
  if (!rows.length)
    return (
      <Empty
        loading={loading}
        done={intl.formatMessage({
          id: "grow.landing.noRatedPowers",
          defaultMessage: "No rated powers yet.",
        })}
      />
    );
  return (
    <ul className="space-y-1.5">
      {rows.map((r) => {
        const p = powerOf(r.element, intl);
        if (!p) return null;
        const asked = askedIds.has(r.element);
        return (
          <li key={r.element} className="flex items-center gap-1">
            <Link
              to={`/grow/power/${p.abbr}`}
              className="flex min-w-0 flex-1 items-center gap-3 rounded-lg px-2 py-1.5 transition hover:bg-ink/[0.06]"
            >
              <span className="min-w-0 flex-1">
                <span className="truncate text-[13px]">{p.name}</span>
                <span className="mt-0.5 block text-[10px] tracking-[0.15em] text-ink/40">
                  {p.abbr} · {p.group}
                </span>
              </span>
              <span className="h-1.5 w-20 shrink-0 overflow-hidden rounded-full bg-ink/10">
                <span
                  className="block h-full rounded-full bg-accent"
                  style={{ width: `${r.result}%` }}
                />
              </span>
              <span className="w-8 shrink-0 text-right text-[11px] tabular-nums text-ink/55">
                {r.result}
              </span>
            </Link>
            {onAsk ? (
              <button
                type="button"
                onClick={() => {
                  setAskedIds((prev) => new Set(prev).add(r.element));
                  onAsk(p.name);
                }}
                aria-label={intl.formatMessage({ id: "grow.landing.improveAria" }, { name: p.name })}
                aria-pressed={asked}
                title={intl.formatMessage({ id: "grow.landing.improveAria" }, { name: p.name })}
                className={`grid h-8 w-8 shrink-0 place-items-center rounded-full transition hover:bg-ink/[0.06] ${
                  asked ? "bg-ink/[0.06] ring-1 ring-ink/15" : "smycles-ai-shadow bg-page"
                }`}
              >
                <img
                  src="/assistant/smike.svg"
                  alt=""
                  aria-hidden
                  draggable={false}
                  className={`h-5 w-5 select-none transition-transform duration-200 motion-reduce:transition-none ${
                    asked ? "-scale-x-100" : ""
                  }`}
                />
              </button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function Card({
  label,
  right,
  to,
  anchor,
  children,
}: {
  label: string;
  right?: ReactNode;
  to?: string;
  /** Assistant anchor (HighlightElement tool + /tour) — on the card itself, so the whole
   *  region gets the highlight outline. */
  anchor?: string;
  children: ReactNode;
}) {
  return (
    <div className="box flex flex-col bg-surface p-5" data-assistant-anchor={anchor}>
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <div className="text-[10px] tracking-[0.25em] text-ink/55">{label}</div>
        {right ? (
          to ? (
            <Link
              to={to}
              className="text-[10px] uppercase tracking-[0.2em] text-ink/40 transition hover:text-ink"
            >
              {right}
            </Link>
          ) : (
            <div className="text-[10px] tracking-[0.2em] text-ink/40">{right}</div>
          )
        ) : null}
      </div>
      {children}
    </div>
  );
}

function Empty({
  loading,
  done,
}: {
  loading: boolean;
  done?: string;
}) {
  const intl = useIntl();
  return (
    <div className="py-2 text-[12px] text-ink/45">
      {loading
        ? intl.formatMessage({
            id: "grow.landing.loading",
            defaultMessage: "loading…",
          })
        : (done ??
          intl.formatMessage({
            id: "grow.landing.noData",
            defaultMessage: "No data.",
          }))}
    </div>
  );
}
